import React, { FC, useEffect, useState } from 'react';

import { Fab, Grid, Hidden } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import SaveIcon from '@material-ui/icons/Save';

import { ICourse, ITee } from '../../../../../../../../store/admin/courses/store';
import { IRoundTeesAssignation } from '../../../../../../../../store/roundTees/store';

import Select from '../../../../../../../../components/AutoComplete';
import { Input } from '../../../../../../../../components/Input';
import { menAndWomenGenderOptions } from '../../../../../../../../contrib/Utils/genderOptions';
import { getFormattedHandicapValue } from '../../Utils/getFormattedHandicapValue';
import { TeeAssignationRowStyled } from './TeeAssignationRow.Styles';
import { TeeSelector } from '../../../TeeSelector';
import { getTeesOptions, ITeeOption } from '../../../Utils/getTeesOptions';
import { isRoundTeeAssignationInvalid } from '../../Utils/isRoundTeeAssignationValid';

interface IProps {
  course?: ICourse;
  deleteTeeBoxAssignment: (
    idx: number,
    roundTeeAssignation: IRoundTeesAssignation
  ) => () => Promise<any> | void;
  index: number;
  onRoundTeeAssignationChange: (field: string, index: number) => (event: any) => void;
  saveTeeBoxAssignment: (
    index: number,
    roundTeeAssignation: IRoundTeesAssignation
  ) => () => Promise<any>;
  teeAssignation: IRoundTeesAssignation;
  unsavedRoundTeeAssignations: number[];
}

const TeeAssignationRow: FC<IProps> = props => {
  const {
    course,
    deleteTeeBoxAssignment,
    index,
    onRoundTeeAssignationChange,
    saveTeeBoxAssignment,
    teeAssignation,
    unsavedRoundTeeAssignations
  } = props;

  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [selectedTeeOption, setSelectedTeeOption] = useState<ITee>();
  const [isTeeSelectorOpen, setIsTeeSelectorOpen] = useState(false);

  const genderValue =
    teeAssignation.gender === null
      ? menAndWomenGenderOptions.find(option => option.value === 'B')
      : menAndWomenGenderOptions.find(option => option.value === teeAssignation.gender);

  useEffect(() => {
    setSelectedTeeOption(teeAssignation.tee);
  }, [teeAssignation]);

  const getHandicapValue = (handicapField: 'handicapLower' | 'handicapUpper') => {
    const handicapValue = teeAssignation[handicapField];

    return getFormattedHandicapValue(handicapValue || 0);
  };

  const editTee = (option: ITeeOption) => {
    onRoundTeeAssignationChange('tee', index)(option);
    setSelectedTeeOption(option.value);
  };

  useEffect(() => {
    const teeAssignmentIsUnsaved = unsavedRoundTeeAssignations.indexOf(index) < 0;

    const teeAssignationIsInvalid = isRoundTeeAssignationInvalid(teeAssignation);

    setIsTeeSelectorOpen(!teeAssignmentIsUnsaved);

    setIsSaveDisabled(teeAssignmentIsUnsaved || teeAssignationIsInvalid);
  }, [unsavedRoundTeeAssignations, index, teeAssignation]);

  return (
    <TeeAssignationRowStyled className="flight-rows items" container={true} spacing={2}>
      <Grid item={true} xs={2} className="sub-rows sex">
        <Hidden mdUp={true}>
          <label>SEX *</label>
        </Hidden>

        <div className="sub-rows inner">
          <Select
            value={
              teeAssignation.gender === null
                ? menAndWomenGenderOptions.find(option => option.value === 'B')
                : menAndWomenGenderOptions.find(option => option.value === teeAssignation.gender)
            }
            onChange={onRoundTeeAssignationChange('gender', index)}
            color="primary"
            options={menAndWomenGenderOptions}
          />
        </div>
      </Grid>

      <Grid item={true} xs={2} className="sub-rows age">
        <Hidden mdUp={true}>
          <label>AGE</label>
        </Hidden>

        <div className="sub-rows inner">
          <Input
            classes={{ root: 'flights-input' }}
            id={`age-${index}-lower`}
            inputProps={{ min: 0 }}
            fullWidth={true}
            type="number"
            value={teeAssignation.ageLower || ''}
            onChange={onRoundTeeAssignationChange('ageLower', index)}
            label="LOWEST"
          />

          <div className="table-title to-field">TO</div>

          <Input
            classes={{ root: 'flights-input' }}
            fullWidth={true}
            id={`age-${index}-upper`}
            inputProps={{ min: 0 }}
            onChange={onRoundTeeAssignationChange('ageUpper', index)}
            type="number"
            value={teeAssignation.ageUpper || ''}
            label="HIGHEST"
          />
        </div>
      </Grid>

      <Grid item={true} xs={2} className="sub-rows handicaps">
        <Hidden mdUp={true}>
          <label>HANDICAPS</label>
        </Hidden>

        <div className="sub-rows inner">
          <Input
            classes={{ root: 'flights-input' }}
            fullWidth={true}
            id={`hc-${index}-lower`}
            label="LOWEST"
            type="handicap"
            value={getHandicapValue('handicapLower')}
            onChange={onRoundTeeAssignationChange('handicapLower', index)}
          />

          <div className="table-title to-field">TO</div>

          <Input
            classes={{ root: 'flights-input' }}
            fullWidth={true}
            id={`hc-${index}-upper`}
            type="handicap"
            value={getHandicapValue('handicapUpper')}
            onChange={onRoundTeeAssignationChange('handicapUpper', index)}
            label="HIGHEST"
          />
        </div>
      </Grid>

      <Grid item={true} xs={5} className="sub-rows handicaps">
        <TeeSelector
          editMode={isTeeSelectorOpen}
          flightId={teeAssignation.id}
          tee={selectedTeeOption}
          onEdit={editTee}
          options={getTeesOptions({ course, gender: genderValue ? genderValue.value : null })}
        />
      </Grid>

      <Grid item={true} xs={1} className="sub-rows sub-row-actions">
        <Fab
          aria-label="Save Flight"
          className="edit-button"
          color="primary"
          disabled={isSaveDisabled}
          onClick={saveTeeBoxAssignment(index, teeAssignation)}
          size="small"
        >
          <SaveIcon />
        </Fab>

        <Fab
          aria-label="Delete Flight"
          color="primary"
          onClick={deleteTeeBoxAssignment(index, teeAssignation)}
          size="small"
        >
          <DeleteIcon />
        </Fab>
      </Grid>
    </TeeAssignationRowStyled>
  );
};

export default TeeAssignationRow;
